import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RotateCcw, ArrowRight, ShieldCheck, User, Sparkles, FlaskConical } from 'lucide-react';
export function AppHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnDashboard = location.pathname === '/' || location.pathname === '/admin';
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAdminViewMode = localStorage.getItem('viewMode') === 'admin';
  const isAdmin = isAdminRoute || !isOnDashboard && isAdminViewMode;
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDemoPanel, setShowDemoPanel] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);
  const handleReset = () => {
    localStorage.removeItem('newProposalCreated');
    localStorage.removeItem('proposalUploaded_10');
    localStorage.removeItem('uploadedArtifacts_10');
    localStorage.removeItem('newProposalTitle');
    localStorage.removeItem('newProposalMechanism');
    localStorage.removeItem('newProposalFunder');
    localStorage.removeItem('newProposalFoaNumber');
    localStorage.removeItem('newProposalDeadline');
    localStorage.removeItem('artifactsReady_10');
    localStorage.removeItem('artifactsTotal_10');
    localStorage.removeItem('artifactsReadyIds_10');
    localStorage.removeItem('artifactsReady_1');
    localStorage.removeItem('artifactsTotal_1');
    localStorage.removeItem('artifactsReadyIds_1');
    localStorage.removeItem('viewMode');
    setShowDemoPanel(false);
    navigate('/');
    window.location.reload();
  };
  const toggleRole = () => {
    setShowDropdown(false);
    if (isAdmin) {
      localStorage.removeItem('viewMode');
      navigate('/');
    } else {
      localStorage.setItem('viewMode', 'admin');
      navigate('/admin');
    }
  };
  return <>
      <header className="flex-shrink-0 h-14 bg-white border-b border-border flex items-center px-4 sm:px-6 z-20" data-id="element-17">
        <div className={`${isAdmin ? 'max-w-[1600px]' : 'max-w-6xl'} mx-auto w-full flex items-center justify-between`} data-id="element-18">
          {/* Left: Logo + Name */}
          <button onClick={() => navigate(isAdmin ? '/admin' : '/')} className="flex items-center gap-2.5" data-id="element-19">
            <img src="https://cdn.magicpatterns.com/uploads/a7Vt3NeSHt7QpcvRYeaMng/Origami.png" alt="Origami logo" className="h-7 w-7 object-contain" data-id="element-20" />
            <span className="text-base font-bold text-foreground tracking-tight" data-id="element-21">
              Origami
            </span>
            {isAdmin && <span className="ml-2 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold uppercase tracking-wide" data-id="element-22">
                Admin
              </span>}
          </button>

          {/* Right: Nav + Avatar */}
          <div className="flex items-center gap-5" data-id="element-23">
            <button onClick={() => {
            if (!isAdmin) localStorage.removeItem('viewMode');
            navigate(isAdmin ? '/admin' : '/');
          }} className={`text-sm font-medium transition-colors ${isOnDashboard ? 'text-indigo-700' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-24">
              {isAdmin ? 'Proposal Portfolio' : 'My Proposals'}
            </button>

            {/* User Avatar with Dropdown */}
            <div className="relative" ref={dropdownRef} data-id="element-25">
              <button onClick={() => setShowDropdown(!showDropdown)} className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ring-2 ring-transparent hover:ring-indigo-200 transition-all ${isAdmin ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700'}`} data-id="element-26">
                {isAdmin ? 'AD' : 'SC'}
              </button>

              {showDropdown && <div className="absolute top-full right-0 mt-2 w-64 rounded-lg bg-white border shadow-xl z-50 py-1" data-id="element-27">
                  <div className="px-3 py-2.5 border-b border-border" data-id="element-28">
                    <p className="text-sm font-semibold text-foreground" data-id="element-29">
                      {isAdmin ? 'Admin User' : 'Dr. Sarah Chen'}
                    </p>
                    <p className="text-xs text-muted-foreground" data-id="element-30">
                      {isAdmin ? 'admin@research.ucsf.edu' : 'sarah.chen@ucsf.edu'}
                    </p>
                  </div>
                  <div className="py-1" data-id="element-31">
                    <button onClick={toggleRole} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors" data-id="element-32">
                      {isAdmin ? <>
                          <User className="h-4 w-4 text-indigo-600" data-id="element-33" />
                          Switch to PI View
                        </> : <>
                          <ShieldCheck className="h-4 w-4 text-indigo-600" data-id="element-34" />
                          Switch to Admin View
                        </>}
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </header>

      {/* Demo hotspot — bottom-left corner */}
      <div className="fixed bottom-0 left-0 z-50" onMouseEnter={() => setShowDemoPanel(true)} onMouseLeave={() => setShowDemoPanel(false)} data-id="element-35">
        {/* Invisible trigger zone */}
        <div className="w-20 h-20" data-id="element-36" />

        {/* Floating panel */}
        <div className={`absolute bottom-3 left-3 w-56 rounded-lg bg-white border border-border shadow-xl py-1.5 transition-all duration-200 ${showDemoPanel ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none'}`} data-id="element-37">
          <div className="px-3 pb-1.5 mb-0.5 border-b border-border" data-id="element-38">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-39">
              <FlaskConical className="h-3 w-3" data-id="element-40" />
              Demo Controls
            </p>
          </div>
          {!isAdmin && <button onClick={() => {
          localStorage.setItem('newProposalCreated', 'true');
          setShowDemoPanel(false);
          navigate('/proposals/10/timeline');
        }} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors" data-id="element-41">
              <ArrowRight className="h-3.5 w-3.5 text-indigo-500" data-id="element-42" />
              Go to New Proposal
            </button>}
          {!isAdmin && <button onClick={() => {
          localStorage.setItem('newProposalCreated', 'true');
          localStorage.setItem('proposalUploaded_10', 'true');
          setShowDemoPanel(false);
          navigate('/proposals/10/timeline');
        }} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors" data-id="element-43">
              <Sparkles className="h-3.5 w-3.5 text-violet-500" data-id="element-44" />
              Go to Transformed Proposal
            </button>}
          <div className="border-t border-border mt-1 pt-1" data-id="element-45">
            <button onClick={handleReset} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors" data-id="element-46">
              <RotateCcw className="h-3.5 w-3.5" data-id="element-47" />
              Reset Demo
            </button>
          </div>
        </div>
      </div>
    </>;
}